import React, { type HTMLAttributes, useId, useState } from 'react';
import styled, { css } from 'styled-components';
import { IconCircleInfoLinear } from '@/app/common/icons/libraryIcons';
import useUserConfig from '@/app/components/hotel-detail/hooks/useUserConfig';
import { useTranslation } from '@/app/components/translations/Translation';
import { PriceTypeTextX } from '@/app/components/hotel-detail/PriceType';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { Brand } from '@/domainLogic/index.ts';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import customCss from '@/app/components/hotel-detail/css/customCss';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { useIsMobile } from '../hooks/useMediaSSR';
import { keyUpHandler } from '../common/common.helper';
import OpenXModal from '../../search/components/Modal/OpenXModal';
import BasicModalHeader from '../../search/components/Modal/LayoutElements/BasicModalHeader';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { formatPrice } from '../../search/utils/formatting';
import { getDoublePricingAmounts } from './roomCommon';

const LocalTaxesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: inherit;
  flex-direction: column;
  ${(p) => p.theme.kallisto.mixins.font('Caption')};
  color: ${(p) => p.theme.kallisto.color.text.static.textSecondary};

  @media ${customCss.breakpoints.sm} {
    ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
  }
`;

const TaxSection = styled.div`
  border-bottom: 1px dashed ${(p) => p.theme.commons.colors.gray100};
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
`;

const TaxSectionPrice = styled(TextBase)`
  padding-left: 4px;
`;

export const ExcludedChargesLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: inherit;
`;

type DetailedLocalTaxesProps = {
  roomsIncludedInPrice: number;
  nights: number;
  passengers: number;
  hotelOnly: boolean;
  taxes: ExcludedCharges;
  priceWithoutTaxes: number;
  totalPrice: number;
  taxesIncludedInFinalPrice?: boolean;
  showTotalPriceIncludedTaxes?: boolean;
};

const DetailedLocalTaxes = (props: DetailedLocalTaxesProps) => {
  const {
    roomsIncludedInPrice = 1,
    nights = 1,
    passengers,
    hotelOnly,
    taxes,
    priceWithoutTaxes,
    totalPrice,
    taxesIncludedInFinalPrice,
    showTotalPriceIncludedTaxes = false,
  } = props;

  const { t } = useTranslation();
  const { isDoublePriceDP } = useUserConfig();
  const { accommodationOnly } = useUrlSearchParams();
  const { showTotalPrice } = usePriceDisplay();
  const [isPriceBreakdownModalOpen, setPriceBreakdownModalOpen] = useState<boolean | undefined>(false);
  const uid = useId();
  const randomId = `price-breakdown-modal-${uid}`;

  const taxesTotal = accommodationOnly ? taxes.amount : taxes.amount * passengers;
  const labelTaxes = showTotalPrice ? { ...taxes, amount: taxes.amount * passengers } : taxes;
  const doublePricingTotal = showTotalPriceIncludedTaxes
    ? getDoublePricingAmounts({
        pricePerPerson: priceWithoutTaxes,
        taxesPerPerson: taxes.amount,
        passengers,
        isDoublePriceDP,
        showTotalPrice,
      }).total
    : undefined;

  return accommodationOnly ? (
    <>
      <PriceBreakdownModal
        isOpen={isPriceBreakdownModalOpen}
        id={randomId}
        onDismiss={() => setPriceBreakdownModalOpen(false)}
        onOpenByBrowserHistory={() => setPriceBreakdownModalOpen(true)}
        title={
          hotelOnly
            ? t('vacation.common.citytaxes.price.details.title')
            : t('vacation.common.citytaxes.package.price.details.title')
        }
      >
        <PriceBreakdownContent
          roomsIncludedInPrice={roomsIncludedInPrice}
          nights={nights}
          priceWithoutTaxes={priceWithoutTaxes}
          totalPrice={totalPrice}
          taxesIncludedInFinalPrice={taxesIncludedInFinalPrice}
          passengers={passengers}
          taxesTotal={taxesTotal}
          hotelOnly={hotelOnly}
        />
      </PriceBreakdownModal>
      <ExcludedChargesLabelContainer
        role="button"
        tabIndex={0}
        aria-label="taxes breakdown"
        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setPriceBreakdownModalOpen(true);
        }}
        onKeyUp={(event: React.KeyboardEvent<HTMLDivElement>) =>
          keyUpHandler(() => {
            event.stopPropagation();
            setPriceBreakdownModalOpen(true);
          })
        }
      >
        <ExcludedChargesLabel
          taxesIncludedInFinalPrice={taxesIncludedInFinalPrice}
          taxes={labelTaxes}
          doublePricingTotal={doublePricingTotal}
        />

        <IconCircleInfoLinear
          css={`
            flex-shrink: 0;
          `}
        />
      </ExcludedChargesLabelContainer>
    </>
  ) : (
    <ExcludedChargesLabelContainer>
      <ExcludedChargesLabel
        taxesIncludedInFinalPrice={taxesIncludedInFinalPrice}
        taxes={labelTaxes}
        doublePricingTotal={doublePricingTotal}
      />
    </ExcludedChargesLabelContainer>
  );
};

type ExcludedChargesLabelProps = {
  taxesIncludedInFinalPrice?: boolean;
  taxes: ExcludedCharges;
  doublePricingTotal?: number;
};

export function ExcludedChargesLabel(props: ExcludedChargesLabelProps) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { isDoublePriceDP } = useUserConfig();
  const { showTotalPrice } = usePriceDisplay();
  const { taxesIncludedInFinalPrice, taxes, doublePricingTotal } = props;

  if (!taxes) return undefined;

  const taxesIncludedDoublePricing = localization?.configValuesApp?.taxesIncludedDoublePricing;
  if (taxesIncludedDoublePricing && doublePricingTotal != null) {
    const formattedAmount = formatPrice({ price: doublePricingTotal, localization });
    // No "pp" suffix when the amount already covers all travellers.
    const ppSuffix = isDoublePriceDP || showTotalPrice ? '' : ` ${t('vacation.select.room.price.dp.perperson.short')}`;
    return (
      <StyledExcludedChargesLabel
        dangerouslySetInnerHTML={{
          __html: t('vacation.oss.cards.taxes_double_pricing.includes_label.short', {
            amount: `<strong>${formattedAmount}</strong>${ppSuffix}`,
          }),
        }}
      />
    );
  }

  const localTaxesText = `${taxes.amount > 0 && taxes.amount < 1 ? formatPrice({ price: taxes.amount, localization, currency: taxes.currency }) : formatPrice({ price: taxes.amount, localization, currency: taxes.currency })}`;
  const taxesIncludedPopup = localization?.configValuesApp?.taxesIncludedPopup;

  return (
    <StyledExcludedChargesLabel>
      {(!taxesIncludedInFinalPrice || isDoublePriceDP) && localTaxesText
        ? t('vacation.select.rooms.excludedcharges.long', { amount: localTaxesText })
        : taxesIncludedPopup && !isDoublePriceDP
          ? t('vacation.oss.cards.taxes_fees_included.all.title')
          : taxesIncludedPopup
            ? t('vacation.select.rooms.includedcharges.long.dp', { amount: localTaxesText })
            : t('vacation.common.citytaxes.nohiddenfees')}
    </StyledExcludedChargesLabel>
  );
}

export const StyledExcludedChargesLabel = styled.span``;

type PriceBreakdownContentProps = {
  roomsIncludedInPrice: number;
  nights: number;
  passengers: number;
  hotelOnly: boolean;
  priceWithoutTaxes: number;
  totalPrice: number;
  taxesIncludedInFinalPrice?: boolean;
  taxesTotal: number;
} & React.HTMLAttributes<HTMLDivElement>;

export function PriceBreakdownContent(props: PriceBreakdownContentProps) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { isTrain } = useUrlSearchParams();

  const {
    roomsIncludedInPrice = 1,
    nights = 1,
    passengers,
    hotelOnly,
    priceWithoutTaxes,
    totalPrice,
    taxesIncludedInFinalPrice,
    taxesTotal,
    ...rest
  } = props;

  const travellersText =
    passengers > 1 ? t('vacation.common.citytaxes.taxes.travellers') : t('vacation.common.citytaxes.taxes.traveller');
  const nightsText =
    nights > 1 ? t('vacation.common.citytaxes.taxes.nights') : t('vacation.common.citytaxes.taxes.night');

  const totalText =
    roomsIncludedInPrice > 1
      ? t('vacation.common.citytaxes.taxes.total_with_rooms', {
          amount_rooms: roomsIncludedInPrice.toString(),
          amount_nights: nights.toString(),
          nights: nightsText,
          amount_travellers: passengers.toString(),
          travellers: travellersText,
        })
      : t('vacation.common.citytaxes.taxes.total', {
          amount_nights: nights.toString(),
          nights: nightsText,
          amount_travellers: passengers.toString(),
          travellers: travellersText,
        });

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
      `}
      {...rest}
    >
      <TaxSection>
        <TextBase
          css={css`
            font-weight: ${({ theme }) => theme.commons.font.weights.bold};
          `}
        >
          {hotelOnly
            ? t('vacation.common.citytaxes.roomrate')
            : isTrain
              ? t('vacation.common.trainplushotel')
              : t('vacation.common.citytaxes.packagerate')}
        </TextBase>
        <TaxSectionPrice>{formatPrice({ price: priceWithoutTaxes, localization })}</TaxSectionPrice>
      </TaxSection>
      <TaxSection>
        <div>
          <TextBase
            css={css`
              font-weight: ${({ theme }) => theme.commons.font.weights.bold};
              margin-right: 0.25rem;
            `}
          >
            {t('vacation.common.citytaxes.taxes')}
          </TextBase>
          <TextBase>{t('vacation.common.citytaxes.payablehotel')}</TextBase>
        </div>
        <TaxSectionPrice>{formatPrice({ price: taxesTotal, localization })}</TaxSectionPrice>
      </TaxSection>
      {hotelOnly && (
        <TaxSection>
          <TextBase
            css={css`
              font-weight: ${({ theme }) => theme.commons.font.weights.bold};
              margin-right: 0.25rem;
            `}
          >
            {totalText}
          </TextBase>
          <TaxSectionPrice>
            {formatPrice({ price: taxesIncludedInFinalPrice ? totalPrice : totalPrice + taxesTotal, localization })}
          </TaxSectionPrice>
        </TaxSection>
      )}
      <TextBase
        css={`
          padding-top: 16px;
        `}
        dangerouslySetInnerHTML={{
          __html: taxesIncludedInFinalPrice
            ? t('vacation.common.citytaxes.taxes.description')
            : t('vacation.common.excludedcharges.description'),
        }}
      />
    </div>
  );
}

export const StyledPriceTypeContainer = styled.div`
  white-space: nowrap;
`;

type PriceTypeContainerProps = {
  roomsIncludedInPrice: number;
  price: number | undefined;
  nights: number | undefined;
} & HTMLAttributes<HTMLElement>;

export function PriceTypeContainer(props: PriceTypeContainerProps) {
  const { roomsIncludedInPrice, price, nights, ...rest } = props;
  const { isDoublePrice, isDoublePriceDP } = useUserConfig();
  return isDoublePrice || isDoublePriceDP ? (
    <StyledPriceTypeContainer
      {...rest}
      data-testid="price-type-container"
      css={css`
        color: ${
          isDoublePrice || isDoublePriceDP
            ? (p) => p.theme.commons.colors.gray800
            : (p) => p.theme.commons.colors.gray400
        };

        ${
          isDoublePrice || isDoublePriceDP
            ? `
            font-weight: bold;
          `
            : ''
        }
      `}
    >
      <PriceTypeTextX
        nights={nights}
        price={price}
        roomsIncludedInPrice={roomsIncludedInPrice}
        isDoublePriceDP={isDoublePriceDP}
      />
    </StyledPriceTypeContainer>
  ) : null;
}

export type LocalTaxesProps = {
  nights: number | undefined;
  passengers: number;
  roomsIncludedInPrice: number;
  excludedChargesIncluded: boolean | undefined;
  excludedCharges: ExcludedCharges | undefined;
  price: number;
  priceWithoutTaxes?: number;
  showDoublePrice?: boolean;
  showTotalPriceIncludedTaxes?: boolean;
} & React.HTMLAttributes<HTMLElement>;

export function LocalTaxes(props: LocalTaxesProps) {
  const {
    nights = 1,
    passengers,
    roomsIncludedInPrice,
    priceWithoutTaxes,
    price,
    excludedCharges,
    excludedChargesIncluded,
    showDoublePrice = true,
    showTotalPriceIncludedTaxes = false,
    ...rest
  } = props;

  const { accommodationOnly } = useUrlSearchParams();

  return (
    <LocalTaxesContainer {...rest}>
      {showDoublePrice && (
        <PriceTypeContainer nights={nights} price={price} roomsIncludedInPrice={roomsIncludedInPrice} />
      )}

      {excludedCharges && excludedCharges.amount > 0 && priceWithoutTaxes ? (
        <DetailedLocalTaxes
          hotelOnly={accommodationOnly}
          nights={nights}
          passengers={passengers}
          priceWithoutTaxes={priceWithoutTaxes}
          roomsIncludedInPrice={roomsIncludedInPrice}
          taxes={excludedCharges}
          taxesIncludedInFinalPrice={excludedChargesIncluded}
          totalPrice={price}
          showTotalPriceIncludedTaxes={showTotalPriceIncludedTaxes}
        />
      ) : undefined}
    </LocalTaxesContainer>
  );
}

type PriceBreakdownModalProps = {
  children: React.ReactNode;
  id: string;
  isOpen: boolean | undefined;
  title: string;
  onDismiss: () => void;
  onOpenByBrowserHistory?: () => void;
};

export function PriceBreakdownModal(props: PriceBreakdownModalProps) {
  const { children, title, isOpen, onDismiss, onOpenByBrowserHistory, id } = props;
  const { staticData, commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);

  const onModalClose = (e?: object) => {
    (e as Partial<Event> | undefined)?.stopPropagation?.();
    onDismiss();
  };

  return (
    <OpenXModal
      isOpen={isOpen ?? false}
      isMobile={isMobile ?? true}
      brand={staticData.domainValues?.brand as Brand}
      // stopPropagation to avoid opening the room modal when closing the taxes modal
      onClose={onModalClose}
      onCloseByBrowserHistory={onDismiss}
      onOpenByBrowserHistory={onOpenByBrowserHistory}
      fullScreen={false}
      id={id}
      aria-label={title}
      bodyBackgroundColor="backgroundPage"
      header={{
        sticky: true,
        element: <BasicModalHeader handleClose={onModalClose}>{title}</BasicModalHeader>,
      }}
    >
      {children}
    </OpenXModal>
  );
}
